import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'reactstrap';
import { get } from 'lodash-es';
/*  Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Constants */
import { CAPITAL_ONE_LOGO_SRC } from 'site-modules/financing/constants/financing';
/* Utils */
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { DEFAULT_WIDTH, getPhotoConfig } from 'site-modules/shared/utils/inventory-image-utils';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { getSelectedOffer } from 'client/site-modules/shared/utils/financing/financing';
import { parseMonthlyPayment } from 'site-modules/financing/utils/financing-utils';
/* Components */
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';
import { OdometerWrapperAsync } from 'site-modules/shared/components/odometer-wrapper/odometer-wrapper-async';
import { DotSpinner } from 'site-modules/shared/components/dot-spinner/dot-spinner';

import './payment-card.scss';

export function PaymentCard({
  vehicle = null,
  vdpLink = '',
  financingOffers = [],
  isOffersAvailable = false,
  isLoading = true,
  calculatorSelections = {},
  vin = '',
}) {
  const { make, model, year, trim, bodyType } = get(vehicle, 'vehicleInfo.styleInfo', {});
  const isCpoType = isCpo(vehicle.type);
  const defaultPhoto = getNoVehicleImageToDisplay(bodyType);
  const { photoUrls } = getPhotoConfig(vehicle, DEFAULT_WIDTH, null);
  const photoUrl = photoUrls[0];
  const selectedOffer = getSelectedOffer(financingOffers, calculatorSelections);
  const paymentValue = get(selectedOffer, 'financeInformation.monthlyPayment') || 0;
  const decimalDigits = 2;
  const payment = paymentValue.toLocaleString('en-US', {
    maximumFractionDigits: decimalDigits,
    minimumFractionDigits: decimalDigits,
  });

  // `${payment}1` is a workaround until the odometer formatting issue(https://github.com/HubSpot/odometer/issues/63) will not be fixed
  const monthlyPayment = Number(`${parseMonthlyPayment(payment)}1`);
  const zipCode = get(selectedOffer, 'vehicleStructure.taxesAndFees.zipCode', '');

  return (
    <Card className="payment-card">
      <div className="p-1 text-center">
        <img
          className="d-block mx-auto mb-1"
          width={89}
          height={32}
          src={getStaticImageUrl(CAPITAL_ONE_LOGO_SRC)}
          alt="Capital One"
        />
        <div className="size-14 fw-medium">Your Prequalified Payment</div>
        {isLoading ? (
          <div className="payment-wrapper mt-0_5 bg-gray-lighter mx-auto">
            <DotSpinner classNameWrapper="mx-auto" width={32} size="md" />
          </div>
        ) : (
          <>
            {!isOffersAvailable ? (
              <div>Adjust terms to see your payments</div>
            ) : (
              <Fragment>
                <div className="text-primary-darker fw-bold my-0_25 size-30">
                  $
                  <OdometerWrapperAsync
                    value={monthlyPayment}
                    classNames="monthly-payment-value d-inline-block"
                    duration={200}
                    format="(,ddd).ddd"
                  />
                  <span className="size-16">/mo</span>
                </div>

                <div className="small">{get(selectedOffer, 'financeInformation.loanTerm', 0)} month term</div>
              </Fragment>
            )}
          </>
        )}

        <p className="disclaimer size-12 mb-0 mt-1">
          {zipCode ? `Includes estimated taxes and fees for zip code ${zipCode}` : 'Taxes and fees not included'}
        </p>
      </div>
      <div className="vehicle-info-wrapper p-1">
        <Link
          className="d-flex justify-content-between vehicle-info"
          to={vdpLink}
          rel="nofollow"
          aria-disabled={false}
          tabIndex={0}
          target="_blank"
        >
          <Image
            className="rounded vehicle-img"
            imageId={photoUrl}
            alt={`${make} ${model} with VIN: ${vin}`}
            errorImage={defaultPhoto}
            source="static"
            width={80}
            height={48}
            lazy
          />
          <div className="ms-0_75 size-12 text-info">
            {isCpoType ? 'Certified ' : ''}
            {year} {make} {model} {trim}
          </div>
        </Link>
      </div>
    </Card>
  );
}

PaymentCard.propTypes = {
  vdpLink: PropTypes.string,
  vehicle: InventoryEntities.InventoryVin,
  financingOffers: PropTypes.arrayOf(PropTypes.shape({})),
  isOffersAvailable: PropTypes.bool,
  isLoading: PropTypes.bool,
  calculatorSelections: PropTypes.shape({}),
  vin: PropTypes.string,
};
